<div class="refugee-verification-report">
  <PageControl
    :title="'Refugee Verification Report'"
    :elements_query="sheet_elements"
  >
    <button
      @click="show_export_modal"
      class="btn btn-bg-green text-xs rounded-small shadow-sm me-2"
    >
      <i class="bi bi-download"></i>
      Export Data
    </button>
  </PageControl>

  <!-- Export Modal - Teleported to body to prevent z-index issues -->
  <Teleport to="body">
    <div
      class="modal fade"
      :id="'export-modal-' + report_reference"
      tabindex="-1"
      aria-labelledby="exportModalLabel"
      aria-hidden="true"
    >
      <div class="modal-dialog modal-dialog-centered">
        <div class="modal-content rounded-4 shadow">
          <div class="modal-header">
            <h6 class="modal-title fw-semibold text-muted">
              Export Refugee Verification Report Data
            </h6>
          </div>
          <div class="modal-body">
            <div class="mb-3">
              <label class="form-label fw-bold">Select Data to Export:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.personal_details"
                  id="personalDetails"
                />
                <label class="form-check-label" for="personalDetails">
                  Personal Details & Bio Information
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.status_information"
                  id="statusInformation"
                />
                <label class="form-check-label" for="statusInformation">
                  Status & Application Information
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.permit_details"
                  id="permitDetails"
                />
                <label class="form-check-label" for="permitDetails">
                  Permit & Document Details
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="checkbox"
                  v-model="export_options.raw_json"
                  id="rawJson"
                />
                <label class="form-check-label" for="rawJson">
                  Raw JSON Response Data
                </label>
              </div>
            </div>
            <div class="mb-3">
              <label class="form-label fw-bold">Export Format:</label>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="csv"
                  id="csvFormat"
                />
                <label class="form-check-label" for="csvFormat">
                  CSV (Separate files for each table)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="excel"
                  id="excelFormat"
                />
                <label class="form-check-label" for="excelFormat">
                  Excel (Multiple sheets in one file)
                </label>
              </div>
              <div class="form-check">
                <input
                  class="form-check-input"
                  type="radio"
                  v-model="export_format"
                  value="json"
                  id="jsonFormat"
                />
                <label class="form-check-label" for="jsonFormat">
                  JSON (JavaScript Object Notation)
                </label>
              </div>
            </div>
          </div>
          <div class="modal-footer">
            <button
              type="button"
              class="btn btn-bg-secondary text-xs rounded-small shadow-sm me-2"
              @click="close_modal"
            >
              Cancel
            </button>
            <button
              type="button"
              class="btn btn-bg-green text-xs rounded-small shadow-sm"
              @click="export_data"
              :disabled="!has_selected_tables"
            >
              <i class="bi bi-download me-1"></i>
              Export Data
            </button>
          </div>
        </div>
      </div>
    </div>
  </Teleport>

  <PageComponent>
    <SectionSheet>
      <div
        class="container-fluid py-3 header d-flex align-items-center justify-content-between border-bottom mb-3"
      >
        <div class="d-flex">
          <img
            class="logo"
            width="295"
            height="125"
            src="@/assets/images/new_black_logo2.svg"
            alt="Logo"
          />
        </div>
        <div class="d-flex text-end">
          <div>
            <div class="h4 fw-bold mb-1">Refugee Verification</div>
            <div class="text-muted text-xs">
              28 Fricker Rd,<br />
              Illovo, Johannesburg,<br />
              2196 South Africa<br />
              Website:
              <a
                href="https://www.goodpeopledata.com/"
                target="_blank"
                class="text-decoration-none text-muted"
                >www.goodpeopledata.com</a
              >
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Refugee Verification Summary</h6>
        </div>
        <div class="card-body">
        <p>
            This report documents the verification conducted for
            <span class="fw-bold text-dark">{{ full_name }}</span>,
            a <span class="fw-bold text-dark">{{ age }}-year-old {{ formatted_gender }}</span>
            from {{ country }}, holding the following nationality: <b>{{ nationality }}</b> .
        </p>
        <p>
            Verification was performed by querying the National Immigration Information System (NIIS).
            A total of <span class="fw-bold text-dark">{{ results_count }}</span> record(s) were returned.
        </p>

          <div class="card shadow-none p-2 border-0 bg-white mb-2">
            <div class="card-body text-center">
              <div class="provider-box">
                Verification Status:
                <strong :class="verification_status_class"
                  >{{ verification_status_text }}</strong
                >
              </div>
            </div>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Personal Information</h6>
        </div>
        <div class="card-body">
          <div class="row" v-if="has_refugee_data">
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted small">First Name</label>
                <div class="fw-bold">{{ first_name }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small">Surname</label>
                <div class="fw-bold">{{ surname }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small">Gender</label>
                <div class="fw-bold">{{ formatted_gender }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small">Birth Date</label>
                <div class="fw-bold">{{ formatted_birth_date }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small">Age</label>
                <div class="fw-bold">{{ age }} years old</div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted small">Country</label>
                <div class="fw-bold">{{ country }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small">Nationality</label>
                <div class="fw-bold">{{ nationality }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >NIIS File Number</label
                >
                <div class="fw-bold">{{ niis_file_number }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >Alternative File Number</label
                >
                <div class="fw-bold">{{ alternative_file_number }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >Refugee ID Number</label
                >
                <div class="fw-bold">
                  {{ refugee_id_number || 'Not Available' }}
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center text-muted py-4">
            <i class="fas fa-user-times fa-2x mb-2"></i>
            <p>No personal information available</p>
          </div>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>
    <SectionSheet>
      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Application & Status Information</h6>
        </div>
        <div class="card-body">
          <div class="row" v-if="has_refugee_data">
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >Office of Application</label
                >
                <div class="fw-bold">{{ office_of_application }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >Current Status</label
                >
                <div class="fw-bold">
                  <span :class="status_badge_class">{{ current_status }}</span>
                </div>
              </div>
            </div>
            <div class="col-md-6 d-none">
              <div class="mb-3">
                <label class="form-label text-muted small">Requested By</label>
                <div class="fw-bold">{{ requested_by }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small">Authorized By</label>
                <div class="fw-bold">{{ authorized_by }}</div>
              </div>
            </div>
          </div>

          <div v-else class="text-center text-muted py-4">
            <i class="fas fa-file-times fa-2x mb-2"></i>
            <p>No application information available</p>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Permit & Document Details</h6>
        </div>
        <div class="card-body">
          <div class="row" v-if="has_refugee_data">
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted small">Permit Number</label>
                <div class="fw-bold">{{ permit_number }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small">Expiry Date</label>
                <div class="fw-bold">
                  <span :class="expiry_status_class"
                    >{{ formatted_expiry_date }}</span
                  >
                </div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >Customer Reference</label
                >
                <div class="fw-bold">{{ customer_reference }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >Facial Image Available</label
                >
                <div class="fw-bold">
                  <span :class="facial_image_status_class">
                    <i :class="facial_image_icon" class="me-1"></i>
                    {{ facial_image_status_text }}
                  </span>
                </div>
              </div>
            </div>
          </div>

          <div v-else class="text-center text-muted py-4">
            <i class="fas fa-id-card-times fa-2x mb-2"></i>
            <p>No permit information available</p>
          </div>
        </div>
      </div>

      <div class="card shadow-sm border bg-white mb-2">
        <div class="card-header">
          <h6 class="mb-0">Verification Details</h6>
        </div>
        <div class="card-body">
          <div class="row">
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >Tracking Number</label
                >
                <div class="fw-bold text-muted small">
                  {{ tracking_number }}
                </div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small">Results Count</label>
                <div class="fw-bold">{{ results_count }}</div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="mb-3">
                <label class="form-label text-muted small">Error Message</label>
                <div class="fw-bold">{{ error_message || 'None' }}</div>
              </div>
              <div class="mb-3">
                <label class="form-label text-muted small"
                  >Response Message</label
                >
                <div class="fw-bold">{{ response_message }}</div>
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>
    <SectionSheet>
      <div class="card shadow-sm border bg-white mb-4 p-2">
        <div class="card-header">
          <h6 class="mb-0">Disclaimer & Important Information</h6>
        </div>
        <div class="card-body">
          <p class="text-xs mb-2">
            <strong>External Data Source:</strong> 
            This Refugee Verification Report contains unmodified data 
            and has not been altered in any way for this report by
            <a
              href="https://www.goodpeopledata.com/"
              target="_blank"
              rel="noopener"
            >
              <strong>THE GOOD PEOPLE DATA COMPANY (PTY) LTD.</strong>
            </a>
          </p>
          <p class="text-xs mb-2">
            <strong>Data Source:</strong> The search is performed against the
            National Immigration Information System (NIIS). The verification results depend on the
            quality and completeness of the data available in the NIIS database
            at the time of verification.
          </p>
        </div>
        <div class="provider-box my-3 text-center">
          Refugee Verification report was generated by
          <a
            href="https://www.goodpeopledata.com/"
            target="_blank"
            rel="noopener"
          >
            <strong>THE GOOD PEOPLE DATA COMPANY (PTY) LTD.</strong>
          </a>
        </div>
      </div>

      <div class="report-meta">
        <div><span class="text-muted">Ref:</span> {{ report_reference }}</div>
        <div>
          <span class="text-muted">Generated:</span> {{ generated_date }}
        </div>
      </div>
    </SectionSheet>
  </PageComponent>
</div>
